import { useForm } from 'components/form/Form';
import { classNames } from 'libs/react';
import { useQuery } from 'libs/react';
import { useState } from 'libs/react';
import { Button } from 'components/Button';
import { ButtonGroup } from 'components/ButtonGroup';
import { CustomFormField } from 'components/field/form/CustomFormField';
import { Form } from 'components/form/Form';
import { FormGroup } from 'components/form/FormGroup';
import { TableFilterGroup } from 'components/table/private/TableFilterGroup';
import { Text } from 'components/Text';
import { Closeable } from 'components/util/Closeable';
import { Waiter } from 'components/Waiter';
import { FieldGroupsValuesQuery } from 'graphql/FieldGroup/queries/FieldGroupsValuesQuery';
import { React } from 'libs/react';
import { useMemo } from 'react';
import { FormSelect } from 'components/form/FormSelect';
import { FormInput } from 'components/form/FormInput';
import { FormDate } from 'components/form/FormDate';
import './TableFilter.scss';
import { FormField } from 'components/form/FormField';
import { FormMultiSelect } from 'components/form/FormMultiSelect';
/**
 * @component TableFilter
 * @since 1.0.0
 */
export function TableFilter(props) {
    const { style, field, filterable, filters, value, initialFilter, initialOperator } = Object.assign({ style: 'normal' }, props);
    const [opened, setOpened] = useState(false);
    const [count, setCount] = useState(0);
    const [operator, setOperator] = useState(initialOperator || 'AND');
    const form = useForm();
    const [data, ready, error] = useQuery(FieldGroupsValuesQuery, {
        variables: {
            input: {
                query: [
                    {
                        field,
                        value
                    }
                ]
            }
        }
    });
    // Initialize form values from initialFilter before fields are rendered
    React.useLayoutEffect(() => {
        if (!initialFilter) {
            return;
        }
        // Don't restore if initialFilter is empty (might be after a reset)
        if ((!initialFilter.default || initialFilter.default.length === 0) &&
            (!initialFilter.fields || initialFilter.fields.length === 0)) {
            return;
        }
        // Initialize default filter values
        if (filters && initialFilter.default && initialFilter.default.length > 0) {
            for (const filterValue of initialFilter.default) {
                const filter = filters.find((f) => f.field === filterValue.field);
                if (filter && form.getValue(filter.tag) == null) {
                    form.updateValue(filter.tag, filterValue.value);
                }
            }
        }
        // Initialize field filter values (will be set when data is ready)
        if (initialFilter.fields &&
            initialFilter.fields.length > 0 &&
            (data === null || data === void 0 ? void 0 : data.fieldGroups)) {
            const fieldValuesMap = {};
            for (const fieldValue of initialFilter.fields) {
                if (!fieldValuesMap[fieldValue.field]) {
                    fieldValuesMap[fieldValue.field] = [];
                }
                fieldValuesMap[fieldValue.field].push(fieldValue.value);
            }
            for (const group of data.fieldGroups) {
                for (const field of group.fields) {
                    const fieldId = field.id;
                    const values = fieldValuesMap[fieldId];
                    const fieldName = `fields.${fieldId}`;
                    if (values && values.length > 0 && form.getValue(fieldName) == null) {
                        if (field.type === 'MULTISELECT') {
                            const multiselectObj = {};
                            for (const val of values) {
                                multiselectObj[val] = true;
                            }
                            form.updateValue(fieldName, multiselectObj);
                        }
                        else {
                            form.updateValue(fieldName, values[0]);
                        }
                    }
                }
            }
        }
    }, [initialFilter, filters, form, data === null || data === void 0 ? void 0 : data.fieldGroups]);
    /**
     * @function onChange
     * @since 1.0.0
     */
    function onChange(values) {
        let count = 0;
        const fields = values.fields;
        for (const filter in filters) {
            const f = filters[filter];
            if (values[f.tag] != null && values[f.tag] != '') {
                count++;
            }
        }
        if (fields != null) {
            const filled = (value) => value != '' && value != null;
            count += Object.values(fields).filter(filled).length;
        }
        setCount(count);
    }
    /**
     * @function restoreFormValues
     * @since 1.0.0
     * @hidden
     */
    const restoreFormValues = React.useCallback(() => {
        if (!initialFilter || !ready) {
            return;
        }
        const formValues = {};
        // Restore default filter values (from filters prop)
        if (filters && initialFilter.default && initialFilter.default.length > 0) {
            for (const filterValue of initialFilter.default) {
                const filter = filters.find((f) => f.field === filterValue.field);
                if (filter) {
                    formValues[filter.tag] = filterValue.value;
                    // Update form value directly for each filter field
                    form.updateValue(filter.tag, filterValue.value);
                }
            }
        }
        // Restore field filter values
        if (initialFilter.fields &&
            initialFilter.fields.length > 0 &&
            (data === null || data === void 0 ? void 0 : data.fieldGroups)) {
            const fieldsObj = {};
            // Group field values by field ID
            const fieldValuesMap = {};
            for (const fieldValue of initialFilter.fields) {
                if (!fieldValuesMap[fieldValue.field]) {
                    fieldValuesMap[fieldValue.field] = [];
                }
                fieldValuesMap[fieldValue.field].push(fieldValue.value);
            }
            // Find the field definition to determine its type
            for (const group of data.fieldGroups) {
                for (const field of group.fields) {
                    const fieldId = field.id;
                    const values = fieldValuesMap[fieldId];
                    if (values && values.length > 0) {
                        const fieldName = `fields.${fieldId}`;
                        // For MULTISELECT, values are stored as object with true values
                        if (field.type === 'MULTISELECT') {
                            const multiselectObj = {};
                            for (const val of values) {
                                multiselectObj[val] = true;
                            }
                            fieldsObj[fieldId] = multiselectObj;
                            form.updateValue(fieldName, multiselectObj);
                        }
                        else {
                            // For other field types (SELECT_DROPDOWN, TEXTBOX, etc.), store the value directly
                            const valueToSet = values[0];
                            fieldsObj[fieldId] = valueToSet;
                            form.updateValue(fieldName, valueToSet);
                        }
                    }
                }
            }
            if (Object.keys(fieldsObj).length > 0) {
                formValues.fields = fieldsObj;
            }
        }
        // Trigger onChange to update count
        if (Object.keys(formValues).length > 0) {
            let count = 0;
            const fields = formValues.fields;
            if (filters) {
                for (const filter of filters) {
                    if (formValues[filter.tag] != null && formValues[filter.tag] != '') {
                        count++;
                    }
                }
            }
            if (fields != null) {
                const filled = (value) => value != '' && value != null;
                count += Object.values(fields).filter(filled).length;
            }
            setCount(count);
        }
    }, [initialFilter, ready, filters, form, data === null || data === void 0 ? void 0 : data.fieldGroups]);
    // Update form values when initialFilter changes (for when user returns to the table)
    React.useEffect(() => {
        if (!initialFilter || !ready) {
            return;
        }
        // If initialFilter is empty, clear the form
        if ((!initialFilter.default || initialFilter.default.length === 0) &&
            (!initialFilter.fields || initialFilter.fields.length === 0)) {
            // Clear default filter values
            if (filters) {
                for (const filter of filters) {
                    form.updateValue(filter.tag, '');
                }
            }
            // Clear field filter values
            if (data === null || data === void 0 ? void 0 : data.fieldGroups) {
                for (const group of data.fieldGroups) {
                    for (const field of group.fields) {
                        const fieldName = `fields.${field.id}`;
                        if (field.type === 'MULTISELECT') {
                            form.updateValue(fieldName, null);
                        }
                        else {
                            form.updateValue(fieldName, '');
                        }
                    }
                }
            }
            restoreFormValues();
            return;
        }
        // Update default filter values
        if (filters && initialFilter.default && initialFilter.default.length > 0) {
            for (const filterValue of initialFilter.default) {
                const filter = filters.find((f) => f.field === filterValue.field);
                if (filter) {
                    const currentValue = form.getValue(filter.tag);
                    if (currentValue !== filterValue.value) {
                        form.updateValue(filter.tag, filterValue.value);
                    }
                }
            }
        }
        // Update field filter values when data is ready
        if (initialFilter.fields &&
            initialFilter.fields.length > 0 &&
            (data === null || data === void 0 ? void 0 : data.fieldGroups)) {
            const fieldValuesMap = {};
            for (const fieldValue of initialFilter.fields) {
                if (!fieldValuesMap[fieldValue.field]) {
                    fieldValuesMap[fieldValue.field] = [];
                }
                fieldValuesMap[fieldValue.field].push(fieldValue.value);
            }
            for (const group of data.fieldGroups) {
                for (const field of group.fields) {
                    const fieldId = field.id;
                    const values = fieldValuesMap[fieldId];
                    const fieldName = `fields.${fieldId}`;
                    if (values && values.length > 0) {
                        const currentValue = form.getValue(fieldName);
                        let valueToSet;
                        if (field.type === 'MULTISELECT') {
                            const multiselectObj = {};
                            for (const val of values) {
                                multiselectObj[val] = true;
                            }
                            valueToSet = multiselectObj;
                        }
                        else {
                            valueToSet = values[0];
                        }
                        // Only update if value has changed
                        if (JSON.stringify(currentValue) !== JSON.stringify(valueToSet)) {
                            form.updateValue(fieldName, valueToSet);
                        }
                    }
                }
            }
        }
        // Update count
        restoreFormValues();
    }, [
        initialFilter,
        ready,
        filters,
        form,
        data === null || data === void 0 ? void 0 : data.fieldGroups,
        restoreFormValues
    ]);
    /**
     * @function onToggle
     * @since 1.0.0
     */
    function onToggle() {
        const newOpened = !opened;
        setOpened(newOpened);
        // Restore values when opening the filter
        if (newOpened) {
            setTimeout(() => {
                restoreFormValues();
            }, 50);
        }
    }
    /**
     * @function onClose
     * @since 1.0.0
     */
    function onClose() {
        setOpened(false);
    }
    /**
     * @function onApply
     * @since 1.0.0
     */
    function onApply() {
        setOpened(false);
    }
    /**
     * @function onReset
     * @since 1.0.0
     */
    function onReset() {
        // Clear all form values
        // Clear default filter values
        if (filters) {
            for (const filter of filters) {
                form.updateValue(filter.tag, '');
            }
        }
        // Clear field filter values when data is ready
        if (data === null || data === void 0 ? void 0 : data.fieldGroups) {
            for (const group of data.fieldGroups) {
                for (const field of group.fields) {
                    const fieldName = `fields.${field.id}`;
                    if (field.type === 'MULTISELECT') {
                        form.updateValue(fieldName, null);
                    }
                    else {
                        form.updateValue(fieldName, '');
                    }
                }
            }
        }
        // Call onSubmit directly with empty values to trigger filter update
        // This ensures the filter is updated immediately with empty values
        if (props.onFilter != null) {
            props.onFilter({ default: [], fields: [] }, operator);
        }
        setOpened(false);
    }
    /**
     * @function onSubmit
     * @since 1.0.0
     */
    function onSubmit(values) {
        if (props.onFilter == null) {
            return;
        }
        let fields = values.fields;
        if (fields == null) {
            fields = {};
        }
        const defaultFilterValues = [];
        if (filters != null) {
            for (const filter of filters) {
                if (values[filter.tag] == null || values[filter.tag] == '') {
                    continue;
                }
                defaultFilterValues.push({
                    field: filter.field,
                    value: values[filter.tag],
                    operator: filter.operator
                });
            }
        }
        const fieldsFilterValues = [];
        for (const key in fields) {
            if (typeof fields[key] === 'object') {
                for (const subKey in fields[key]) {
                    if (fields[key][subKey] === true) {
                        fieldsFilterValues.push({ field: key, value: subKey });
                    }
                    else if (fields[key][subKey] !== '' &&
                        fields[key][subKey] !== null) {
                        fieldsFilterValues.push({ field: key, value: fields[key][subKey] });
                    }
                }
            }
            else if (fields[key] !== '' && fields[key] !== null) {
                fieldsFilterValues.push({ field: key, value: fields[key] });
            }
        }
        props.onFilter({ default: defaultFilterValues, fields: fieldsFilterValues }, operator);
    }
    const className = classNames('table-filter', 'table-filter--' + style, { 'table-filter--opened': opened }, props.className);
    // Calculate initial values for default filters from initialFilter
    const formValuesState = React.useMemo(() => {
        const values = {};
        if (initialFilter && initialFilter.default && filters) {
            for (const filterValue of initialFilter.default) {
                const filter = filters.find((f) => f.field === filterValue.field);
                if (filter) {
                    values[filter.tag] = filterValue.value;
                }
            }
        }
        return values;
    }, [initialFilter, filters]);
    // Create a key based on filter values to force re-render when filters change
    const filtersKey = React.useMemo(() => {
        if (!initialFilter ||
            !initialFilter.default ||
            initialFilter.default.length === 0) {
            return 'no-filters';
        }
        return initialFilter.default.map((f) => `${f.field}:${f.value}`).join('|');
    }, [initialFilter]);
    const tableFilterGroups = useMemo(() => {
        const groups = [];
        // Default filters first — included in deps so options update when async data loads
        if (filters != null) {
            groups.push(React.createElement(TableFilterGroup, { key: `default-${filtersKey}`, title: t('Entity Filters'), expanded: true },
                React.createElement(FormGroup, null, filters.map((filter) => {
                    const initialValue = formValuesState[filter.tag] || undefined;
                    if (filter.type === 'text') {
                        return (React.createElement(FormField, { key: `${filter.tag}-${filtersKey}`, ratio: "4" },
                            React.createElement(FormInput, { name: filter.tag, label: filter.label, size: "small", value: initialValue })));
                    }
                    if (filter.type === 'number') {
                        return (React.createElement(FormField, { key: `${filter.tag}-${filtersKey}`, ratio: "4" },
                            React.createElement(FormInput, { name: filter.tag, label: filter.label, type: "number", size: "small", value: initialValue })));
                    }
                    if (filter.type === 'date') {
                        return (React.createElement(FormField, { key: `${filter.tag}-${filtersKey}`, ratio: "4" },
                            React.createElement(FormDate, { name: filter.tag, label: filter.label, time: false, size: "small", value: initialValue })));
                    }
                    if (filter.type === 'select' && filter.options) {
                        return (React.createElement(FormField, { key: `${filter.tag}-${filtersKey}`, ratio: "4" },
                            React.createElement(FormSelect, { name: filter.tag, label: filter.label, options: filter.options, size: "small", value: initialValue })));
                    }
                    if (filter.type === 'multi-select' && filter.options) {
                        return (React.createElement(FormField, { key: `${filter.tag}-${filtersKey}`, ratio: "4" },
                            React.createElement(FormMultiSelect, { name: filter.tag, label: filter.label, options: filter.options, size: "small", value: initialValue })));
                    }
                    return null;
                }))));
        }
        // Field groups
        if (data === null || data === void 0 ? void 0 : data.fieldGroups) {
            for (const group of data.fieldGroups) {
                groups.push(React.createElement(TableFilterGroup, { key: group.id, title: group.title, infos: group.infos },
                    React.createElement(FormGroup, null, group.fields
                        .filter((field) => 
                    // include only filterable field types
                    field.type === 'MULTISELECT' ||
                        field.type === 'SELECT_DROPDOWN' ||
                        field.type === 'TEXTBOX' ||
                        field.type === 'NUMBER' ||
                        field.type === 'DATE' ||
                        field.type === 'DATETIME' ||
                        field.type === 'TIME' ||
                        field.type === 'YEAR')
                        .map((field) => (React.createElement(FormField, { key: field.id, ratio: "4" },
                        React.createElement(CustomFormField, { field: field, noDefaultValue: true, includeInactiveOptions: true, size: "small" })))))));
            }
        }
        return groups.length > 0 ? groups : undefined;
    }, [data === null || data === void 0 ? void 0 : data.fieldGroups, initialFilter, filters, filtersKey, formValuesState]);
    return (React.createElement(Closeable, { onClose: onClose },
        React.createElement("div", { className: className },
            React.createElement(Form, { onChange: onChange, onSubmit: onSubmit },
                filterable && (React.createElement(Button, { className: "table-filter-button", style: "primary", label: t('Filters'), picto: "filter", badge: React.createElement(Text, { value: String(count) }), onPress: onToggle })),
                React.createElement("div", { className: "table-filter-list" },
                    React.createElement("div", { className: "table-filter-list-head" },
                        React.createElement("div", { className: "table-filter-list-title" }, t('')),
                        React.createElement("div", { className: "table-filter-list-tools" },
                            React.createElement(ButtonGroup, null,
                                React.createElement(TableFilterResetButton, { onPress: onReset }),
                                React.createElement(TableFilterApplyButton, { onPress: onApply })))),
                    React.createElement("div", { className: "table-filter-list-body" },
                        React.createElement(Waiter, { until: ready, renderWhileLoading: false, minimumLoadingTime: 0, maximumLoadingTime: 5 }, tableFilterGroups)))))));
}
/**
 * @component TableFilterApplyButton
 * @since 1.0.0
 * @hidden
 */
function TableFilterApplyButton(props) {
    const form = useForm();
    /**
     * @function onPress
     * @since 1.0.0
     */
    function onPress() {
        form.submit();
        requestAnimationFrame(() => {
            if (props.onPress) {
                props.onPress();
            }
        });
    }
    return (React.createElement(Button, { className: "table-filter-list-apply-button", style: "primary", label: t('Filter'), onPress: onPress }));
}
/**
 * @component TableFilterResetButton
 * @since 1.0.0
 * @hidden
 */
function TableFilterResetButton(props) {
    /**
     * @function onPress
     * @since 1.0.0
     */
    function onPress() {
        // Call the parent's onPress handler which will clear values and submit
        if (props.onPress) {
            props.onPress();
        }
    }
    return (React.createElement(Button, { className: "table-filter-list-reset-button", style: "outline", label: t('Reset'), onPress: onPress }));
}
